import React, { useEffect, useState } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { X } from "lucide-react";
import { getPDFDownloadUrl } from "../services/mfuUserService";

const PrintStyles = createGlobalStyle`
  @media print {
    @page {
      size: A4 portrait;
      margin: 10mm 15mm;
    }

    html, body {
      height: auto;
      overflow: visible;
      margin: 0;
      padding: 0;
    }

    * {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      box-sizing: border-box;
    }

    body * {
      visibility: hidden;
    }

    #health-report-modal,
    #health-report-modal * {
      visibility: visible;
    }

    #health-report-modal {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: auto;
      overflow: visible;
    }
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;

  /* Mobile: ไม่มี padding */
  @media (max-width: 768px) {
    padding: 0;
    background-color: rgba(0, 0, 0, 0.9);
  }

  @media print {
    background-color: white;
    position: static;
    padding: 0;
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 0.5rem;
  width: 90vw;
  height: 70vh;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);

  /* Mobile: เต็มหน้าจอเกือบทั้งหมด */
  @media (max-width: 768px) {
    width: 100vw;
    height: 100vh;
    border-radius: 0;
    max-width: 100%;
  }

  @media print {
    width: 100%;
    height: auto;
    max-height: none;
    box-shadow: none;
    overflow: visible;
    border-radius: 0;
    margin: 0;
    padding: 0;
  }
`;

const CloseButton = styled.button`
  position: fixed;
  top: 5vh;
  right: 6vw;
  background: white;
  border: none;
  border-radius: 50%;
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  z-index: 1001;

  &:hover {
    background-color: #f3f4f6;
  }

  @media print {
    display: none;
  }
`;

const PDFButton = styled.button`
  position: fixed;
  top: 5vh;
  right: 12vw;
  background: ${(props) => (props.disabled ? "#9CA3AF" : "#E53E3E")};
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  z-index: 1001;
  font-size: 14px;
  font-weight: 500;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};

  &:hover {
    background-color: ${(props) => (props.disabled ? "#9CA3AF" : "#C53030")};
  }

  @media print {
    display: none;
  }
`;

const ReportContainer = styled.div`
  padding: 0.5rem;
  font-family: "Sarabun", sans-serif;
  overflow: visible;
  transform-origin: top left;
  width: fit-content;
  max-width: 210mm;
  height: fit-content;

  /* Desktop: scale ลงเหลือ 45% */
  transform: scale(0.45);

  /* Tablet: scale ขึ้นเป็น 55% */
  @media (max-width: 1024px) {
    transform: scale(0.55);
  }

  /* Mobile landscape: scale ขึ้นเป็น 65% */
  @media (max-width: 768px) and (orientation: landscape) {
    transform: scale(0.65);
  }

  /* Mobile portrait: scale ขึ้นเป็น 90% */
  @media (max-width: 768px) and (orientation: portrait) {
    transform: scale(0.9);
    padding: 0.25rem;
  }

  /* Small mobile: scale 100% (ไม่ scale) */
  @media (max-width: 480px) {
    transform: scale(1);
    padding: 0.25rem;
    max-width: 100%;
  }

  @media print {
    padding: 0;
    width: 100%;
    height: auto;
    min-height: 0;
    max-height: none;
    box-sizing: border-box;
    transform: none;
    overflow: visible;
  }
`;

const PageNumber = styled.div`
  position: relative;
  text-align: right;
  font-size: 14px;
  font-weight: 400;
  color: #111827;
  margin-bottom: 1rem;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const LogoImage = styled.img`
  height: 60px;
  width: auto;
`;

const TitleSection = styled.div`
  text-align: right;
  flex: 1;
`;

const Title = styled.h1`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0;
  margin-bottom: 0.25rem;
`;

const Subtitle = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: #374151;
`;

const PatientInfoSection = styled.div`
  margin-bottom: 2rem;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const InfoRowLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
`;

const InfoLabel = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: #111827;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 1.5rem 0;
`;

const ContentSection = styled.div`
  display: flex;
  gap: 2rem;
  margin-top: 2rem;
  overflow: visible;

  @media print {
    page-break-inside: avoid;
  }
`;

const LeftSection = styled.div`
  flex: 0 0 250px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const HumanImage = styled.img`
  width: 230px;
  height: auto;
  margin-bottom: 1rem;
`;

const RightSection = styled.div`
  flex: 0 0 400px;
  overflow: visible;
`;

const ResultHeader = styled.div`
  background-image: url("/bg_headbar.png");
  background-size: contain;
  background-position: left center;
  background-repeat: no-repeat;
  color: #000000;
  padding-top: 0.5rem;
  padding-bottom: 1.5rem;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
  font-size: 18px;
  font-weight: 600;
  position: relative;
  margin-bottom: -1.5rem;
  margin-left: -3.4%;
  z-index: 9999;
  overflow: visible;
  min-height: 50px;
`;

const ResultTable = styled.div`
  border: 1px solid #e5e7eb;
  border-top: none;
  border-radius: 0 0 0 0;
  overflow: visible;
`;

const ResultRow = styled.div`
  display: flex;
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }
`;

const ResultLabel = styled.div`
  flex: 1;
  padding: 0.75rem 1rem;
  font-size: 14px;
  font-weight: ${(props) => (props.bold ? "600" : "400")};
  color: #111827;
  background-color: white;
  text-align: left;
`;

const ResultValue = styled.div`
  flex: 1;
  padding: 0.75rem 1rem;
  font-size: 14px;
  font-weight: 400;
  color: #111827;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  background-color: white;
`;

const StatusBadge = styled.span`
  display: inline-block;
  font-size: 14px;
  font-weight: 400;
  color: #111827;
`;

const PageBreak = styled.div`
  @media screen {
    margin-bottom: 2rem;
    padding-bottom: 2rem;
    border-bottom: 2px dashed #e5e7eb;
  }

  @media print {
    page-break-after: always;
    break-after: page;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const AdditionalSection = styled.div`
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  @media print {
    margin-top: 0;
  }
`;

const InfoBox = styled.div`
  border: 2px solid #67c3f3;
  border-radius: 1rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;

  @media print {
    page-break-inside: avoid;
    break-inside: avoid;
    margin-bottom: 1rem;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoBoxHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const InfoIcon = styled.img`
  width: 40px;
  height: 40px;
`;

const InfoBoxTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const InfoBoxContent = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: #374151;
  line-height: 1.6;
  white-space: pre-wrap;
`;

const HealthReportModal = ({ isOpen, onClose, recordData }) => {

  if (!isOpen || !recordData) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };


  const handleSavePDF = () => {
    window.print();
  };

  // คำนวณ BMI Status
  const getBmiStatus = (bmi) => {
    const bmiValue = parseFloat(bmi);
    if (isNaN(bmiValue)) return '-';
    if (bmiValue < 18.5) return 'น้ำหนักต่ำกว่าเกณฑ์';
    if (bmiValue < 23) return 'สมส่วน';
    if (bmiValue < 25) return 'น้ำหนักเกิน';
    if (bmiValue < 30) return 'อ้วนเล็กน้อย';
    return 'อ้วนมาก';
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString || dateString === '-') return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('th-TH', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const recommendations = recordData.recommendations || {};
  const isApproved = recordData.approvalStatus === 'approved';

  // สร้าง data object
  const data = {
    patientName: `${recordData.firstname || ''} ${recordData.lastname || ''}`.trim() || '-',
    age: recordData.age || '-',
    gender: recordData.gender || '-',
    hn: recordData.hn || '-',
    date: formatDate(recordData.checkupDate),
    weight: recordData.weight ? `${recordData.weight} กิโลกรัม` : '-',
    height: recordData.height ? `${recordData.height} เซนติเมตร` : '-',
    bmi: recordData.bmi ? `${recordData.bmi} กก./ม.` : '-',
    bmiStatus: getBmiStatus(recordData.bmi),
    bloodPressure: (recordData.systolic_bp && recordData.diastolic_bp)
      ? `${recordData.systolic_bp}/${recordData.diastolic_bp} มม.ปรอก`
      : '-',
    pulse: recordData.pulse ? `${recordData.pulse} ครั้ง/นาที` : '-',
    waistline: recordData.waistline ? `${recordData.waistline} เซนติเมตร` : '-',
    doctorNote: recommendations.doctor_advice || '-',
    nutritionAdvice: recommendations.nutrition_advice || '',
    exerciseAdvice: recommendations.exercise_advice || '',
    sleepAdvice: recommendations.sleep_advice || '',
  };

  // ✅ เช็คว่าหน้า 2 (คำแนะนำ) มีข้อมูลหรือไม่
  const hasPage2Data =
    (isApproved && data.doctorNote && data.doctorNote !== '-') ||
    (data.nutritionAdvice && data.nutritionAdvice.trim() !== '') ||
    (data.exerciseAdvice && data.exerciseAdvice.trim() !== '') ||
    (data.sleepAdvice && data.sleepAdvice.trim() !== '');

  return (
    <>
      <PrintStyles />
      <ModalOverlay onClick={handleOverlayClick}>
        <PDFButton onClick={handleSavePDF} disabled={!recordData.id}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <line x1="16" y1="13" x2="8" y2="13"></line>
            <line x1="16" y1="17" x2="8" y2="17"></line>
            <polyline points="10 9 9 9 8 9"></polyline>
          </svg>
          PDF
        </PDFButton>
        <CloseButton onClick={onClose}>
          <X size={20} />
        </CloseButton>

        <ModalContent id="health-report-modal">
          <ReportContainer>
            {/* หน้าที่ 1 */}
            <PageNumber>1</PageNumber>
            <Header>
              <LogoSection>
                <LogoImage
                  src="/logo_mfuWC.png"
                  alt="MFU Wellness Center Logo"
                />
              </LogoSection>
              <TitleSection>
                <Title>รายงานผลตรวจสุขภาพ (Health check up report)</Title>
                <Subtitle>แพทย์ผู้ตรวจ {recordData.sent_by || '-'}</Subtitle>
              </TitleSection>
            </Header>

            <PatientInfoSection>
              <InfoRow>
                <InfoLabel>
                  <strong>HN:</strong> {data.hn}
                </InfoLabel>
                <InfoLabel>
                  <strong>ตรวจวันที่:</strong> {data.date}
                </InfoLabel>
              </InfoRow>
              <InfoRow>
                <InfoRowLeft>
                  <InfoLabel>
                    <strong>ชื่อ-นามสกุล:</strong> {data.patientName}
                  </InfoLabel>
                  <InfoLabel>
                    <strong>อายุ:</strong> {data.age} ปี
                  </InfoLabel>
                  <InfoLabel>
                    <strong>เพศ:</strong> {data.gender}
                  </InfoLabel>
                </InfoRowLeft>
              </InfoRow>
            </PatientInfoSection>

            <Divider />

            <ContentSection>
              <LeftSection>
                <HumanImage src="/human.png" alt="Human Body" />
              </LeftSection>

              <RightSection>
                <ResultHeader>ผลการตรวจสุขภาพร่างกายทั่วไป</ResultHeader>
                <ResultTable>
                  <ResultRow>
                    <ResultLabel>น้ำหนัก (Weight)</ResultLabel>
                    <ResultValue>{data.weight}</ResultValue>
                  </ResultRow>
                  <ResultRow>
                    <ResultLabel>ส่วนสูง (Height)</ResultLabel>
                    <ResultValue>{data.height}</ResultValue>
                  </ResultRow>
                  <ResultRow>
                    <ResultLabel>ดัชนีมวลกาย (BMI)</ResultLabel>
                    <ResultValue>{data.bmi}</ResultValue>
                  </ResultRow>
                  <ResultRow>
                    <ResultLabel>• ผลการประเมิน BMI</ResultLabel>
                    <ResultValue>
                      <StatusBadge>{data.bmiStatus}</StatusBadge>
                    </ResultValue>
                  </ResultRow>
                  <ResultRow>
                    <ResultLabel>ความดันโลหิต (BP)</ResultLabel>
                    <ResultValue>{data.bloodPressure}</ResultValue>
                  </ResultRow>
                  <ResultRow>
                    <ResultLabel>ชีพจร (Pulse)</ResultLabel>
                    <ResultValue>{data.pulse}</ResultValue>
                  </ResultRow>
                  <ResultRow>
                    <ResultLabel>รอบเอว (Waistline)</ResultLabel>
                    <ResultValue>{data.waistline}</ResultValue>
                  </ResultRow>
                </ResultTable>
              </RightSection>
            </ContentSection>

            {/* คำแนะนำทั้งหมด - หน้า 2 - แสดงเฉพาะเมื่อมีข้อมูล */}
            {hasPage2Data && (
              <div style={{ pageBreakBefore: 'always', marginTop: 0 }}>
                <PageNumber style={{ marginTop: '0' }}>2</PageNumber>
                <AdditionalSection style={{ marginTop: '0' }}>
                  {/* คำแนะนำจากแพทย์ */}
                  {isApproved && data.doctorNote && data.doctorNote !== '-' && (
                    <InfoBox>
                      <InfoBoxHeader>
                        <InfoIcon src="/ic_2.png" alt="Doctor Advice Icon" />
                        <InfoBoxTitle>คำแนะนำจากแพทย์</InfoBoxTitle>
                      </InfoBoxHeader>
                      <InfoBoxContent>
                        {data.doctorNote}
                      </InfoBoxContent>
                    </InfoBox>
                  )}

                  {/* คำแนะนำด้านโภชนาการ */}
                  {data.nutritionAdvice && (
                    <InfoBox>
                      <InfoBoxHeader>
                        <InfoIcon src="/ic_2.png" alt="Nutrition Advice Icon" />
                        <InfoBoxTitle>คำแนะนำด้านโภชนาการ</InfoBoxTitle>
                      </InfoBoxHeader>
                      <InfoBoxContent>
                        {data.nutritionAdvice}
                      </InfoBoxContent>
                    </InfoBox>
                  )}

                  {/* คำแนะนำด้านการออกกำลังกาย */}
                  {data.exerciseAdvice && (
                    <InfoBox>
                      <InfoBoxHeader>
                        <InfoIcon src="/ic_2.png" alt="Exercise Advice Icon" />
                        <InfoBoxTitle>คำแนะนำด้านการออกกำลังกาย</InfoBoxTitle>
                      </InfoBoxHeader>
                      <InfoBoxContent>
                        {data.exerciseAdvice}
                      </InfoBoxContent>
                    </InfoBox>
                  )}

                  {/* คำแนะนำด้านการนอน */}
                  {data.sleepAdvice && (
                    <InfoBox>
                      <InfoBoxHeader>
                        <InfoIcon src="/ic_2.png" alt="Sleep Advice Icon" />
                        <InfoBoxTitle>คำแนะนำด้านการนอน</InfoBoxTitle>
                      </InfoBoxHeader>
                      <InfoBoxContent>
                        {data.sleepAdvice}
                      </InfoBoxContent>
                    </InfoBox>
                  )}
                </AdditionalSection>
              </div>
            )}
          </ReportContainer>
        </ModalContent>
      </ModalOverlay>
    </>
  );
};

export default HealthReportModal;
